import type { FilterGroup } from '../../core/types/workspaceTypes';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { LooseRelationship } from '../../../../types/electron';
import { getRowValueByColumnName } from './rowValueUtils';
import { safeToISOString } from '../../../../utils/dateUtils';

export type InboundForeignKeyMapping = {
  readonly referencingColumn: string;
  readonly referencedColumn: string;
};

function normalizeFilterValue(value: unknown): string | number | boolean | Date {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Date) {
    return value;
  }
  return JSON.stringify(value);
}

function serializeFilterValue(value: string | number | boolean | Date): string {
  if (value instanceof Date) {
    return safeToISOString(value);
  }
  return String(value);
}

export function getInboundForeignKeyMappings(foreignKey: TableExplorerInboundForeignKey): InboundForeignKeyMapping[] {
  const length = Math.min(foreignKey.referencingColumns.length, foreignKey.referencedColumns.length);
  const mappings: InboundForeignKeyMapping[] = [];
  for (let i = 0; i < length; i += 1) {
    mappings.push({
      referencingColumn: foreignKey.referencingColumns[i],
      referencedColumn: foreignKey.referencedColumns[i],
    });
  }
  return mappings;
}

export function buildInboundForeignKeyFilters(args: {
  readonly foreignKey: TableExplorerInboundForeignKey;
  readonly parentRow: Record<string, unknown>;
  readonly looseRel?: LooseRelationship;
}): { readonly filters: FilterGroup[] | null; readonly missingColumns: string[] } {
  const mappings = getInboundForeignKeyMappings(args.foreignKey);
  const filters: FilterGroup[] = [];
  const missingColumns: string[] = [];

  // Array-FK case: <parent_pk> = ANY(child.array_col). Today this is only
  // supported on Postgres + only for loose FKs (no engine has real array FKs).
  // We always use the first mapping — array FKs are single-column by design.
  const isArrayFk = args.foreignKey.referencingColumnIsArray === true;

  for (const mapping of mappings) {
    const rawValue = getRowValueByColumnName(args.parentRow, mapping.referencedColumn);
    if (rawValue === null || rawValue === undefined) {
      missingColumns.push(mapping.referencedColumn);
      continue;
    }

    const value = normalizeFilterValue(rawValue);
    filters.push({
      field: mapping.referencingColumn,
      conditions: [
        isArrayFk
          ? { operator: 'array_contains', value }
          : { operator: '=', value },
      ],
    });
  }

  if (missingColumns.length > 0) {
    return { filters: null, missingColumns };
  }

  // Append sourceFilter and targetFilter from the loose relationship when present.
  // These narrow the inbound row query to respect polymorphic / soft-delete semantics.
  if (args.looseRel?.sourceFilter) {
    filters.push(...args.looseRel.sourceFilter);
  }
  if (args.looseRel?.targetFilter) {
    filters.push(...args.looseRel.targetFilter);
  }

  return { filters, missingColumns };
}

export function buildInboundForeignKeyFiltersKey(args: {
  readonly foreignKey: TableExplorerInboundForeignKey;
  readonly parentRow: Record<string, unknown>;
}): string | null {
  const mappings = getInboundForeignKeyMappings(args.foreignKey);
  const parts: string[] = [];

  for (const mapping of mappings) {
    const rawValue = getRowValueByColumnName(args.parentRow, mapping.referencedColumn);
    if (rawValue === null || rawValue === undefined) {
      return null;
    }
    parts.push(`${mapping.referencingColumn}=${serializeFilterValue(normalizeFilterValue(rawValue))}`);
  }

  return parts.join('|');
}

